import {
  Box,
  Button,
  Flex,
  type FlexProps,
  Image,
  Typography,
} from "@qws-org/ui-kit";
import type { FC } from "react";

import type { Action, Pages, PaymentMethodsData } from "~/core";
import {
  isExternalLink,
  isInternalLink,
  isTemplateUrl,
} from "~/core/utils/action.utils";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

export type PaymentMethodProps = {
  data: PaymentMethodsData;
  getUrl: (page: Pages) => string;
  onCallAction: (action: Action) => void;
  isControlInactive: (action: Action) => boolean;
} & FlexProps;
export const PaymentMethods: FC<PaymentMethodProps> = ({
  getUrl,
  onCallAction,
  data,
  isControlInactive,
  ...rest
}) => {
  if (!data?.rows || data?.rows?.length === 0) {
    return null;
  }

  const { getDirectusImage } = useDirectus();
  return (
    <Flex flexDirection={"column"} gap={24} {...rest}>
      {data.rows.map((item) => {
        const element = item?.payment_methods_group_id;
        const mGap = element?.mobile_gap ?? 0;
        const mdGap = element?.tablet_gap ?? mGap;
        const dGap = element?.desktop_gap ?? mdGap;
        return (
          <Box>
            {element?.description && (
              <Typography
                horizontalAlign="center"
                fontSize="body.mobile.small"
                md={{
                  fontSize: "body.desktop.small",
                }}
                d={{
                  fontSize: "body.desktop.small",
                }}
                lineHeight="body.mobile.small"
                m={{ mb: 16 }}
                color="text-primary-muted"
              >
                {element?.description}
              </Typography>
            )}
            <Flex
              gap={mGap}
              md={{ gap: mdGap }}
              d={{ gap: dGap }}
              justify={"center"}
              align={"center"}
              flexWrap={"wrap"}
            >
              {element?.items?.map((method) => {
                const mImageWidth =
                  method?.payment_method_id?.image?.mobile_image_width ??
                  undefined;
                const mImageHeight =
                  method?.payment_method_id?.image?.mobile_image_height ??
                  undefined;

                const mdImageWidth =
                  method?.payment_method_id?.image?.tablet_image_width ??
                  mImageWidth;
                const mdImageHeight =
                  method?.payment_method_id?.image?.tablet_image_height ??
                  mImageHeight;
                const dImageWidth =
                  method?.payment_method_id?.image?.desktop_image_width ??
                  mdImageWidth;
                const dImageHeight =
                  method?.payment_method_id?.image?.desktop_image_height ??
                  mdImageHeight;

                const mImage =
                  method?.payment_method_id?.image?.image?.id ?? "";
                const mdImage =
                  method?.payment_method_id?.image?.tablet_image?.id ?? mImage;
                const dImage =
                  method?.payment_method_id?.image?.desktop_image?.id ??
                  mdImage;

                const controlAction = method?.payment_method_id?.action;

                const action = controlAction?.action?.[0];
                const isInactive = controlAction
                  ? isControlInactive(controlAction)
                  : true;
                let href;

                if (isExternalLink(action?.item)) {
                  if (isTemplateUrl(action?.item)) {
                    href = undefined;
                  } else {
                    href = action.item.href ?? undefined;
                  }
                }
                if (isInternalLink(action?.item)) {
                  href = action.item.page
                    ? getUrl(action.item.page)
                    : undefined;
                }
                const handlePressButton = (): void => {
                  if (controlAction) {
                    onCallAction(controlAction);
                  }
                };

                return (
                  !isInactive && (
                    <Button
                      href={href}
                      onPress={handlePressButton}
                      variant="tertiary"
                      p={{ px: 0, py: 0 }}
                    >
                      <Image
                        value={getDirectusImage(mImage, {
                          width: mImageWidth ? mImageWidth * 2 : undefined,
                          height: mImageHeight ? mImageHeight * 2 : undefined,
                          format: "webp",
                          quality: 75,
                        })}
                        mdValue={getDirectusImage(mdImage, {
                          width: mdImageWidth ? mdImageWidth * 2 : undefined,
                          height: mdImageHeight ? mdImageHeight * 2 : undefined,
                          format: "webp",
                          quality: 75,
                        })}
                        dValue={getDirectusImage(dImage, {
                          width: dImageWidth ? dImageWidth * 2 : undefined,
                          height: dImageHeight ? dImageHeight * 2 : undefined,
                          format: "webp",
                          quality: 75,
                        })}
                        maxWidth={mImageWidth}
                        maxHeight={mImageHeight}
                        md={{
                          maxWidth: mdImageWidth,
                          maxHeight: mdImageHeight,
                        }}
                        d={{
                          maxWidth: dImageWidth,
                          height: dImageHeight,
                        }}
                        image={{
                          alt:
                            method?.payment_method_id?.image
                              ?.alternative_text ?? "",
                        }}
                      />
                    </Button>
                  )
                );
              })}
            </Flex>
          </Box>
        );
      })}
    </Flex>
  );
};
